import React, { useState } from 'react';
import { 
  Shield,
  Lock,
  AlertTriangle,
  CheckCircle2,
  Ban,
  Activity,
  Key,
  Eye,
  EyeOff,
  Settings,
  RefreshCw,
} from 'lucide-react';
import { Card } from '../../ui/Card'
import { Badge } from '../../ui/Badge'
import { Button } from '../../ui/Button'
import { cn } from '../../../utils/cn'
import { formatRelativeTime } from '../../../utils/format'

interface SecurityRule {
  id: string;
  name: string;
  type: 'firewall' | 'access-control' | 'rate-limit' | 'encryption';
  status: 'active' | 'disabled' | 'warning';
  action: 'allow' | 'deny' | 'monitor';
  hits: number;
  lastTriggered?: Date;
  severity: 'low' | 'medium' | 'high' | 'critical';
}

interface SecurityIncident {
  id: string;
  type: 'intrusion' | 'unauthorized' | 'anomaly' | 'policy-violation';
  severity: 'low' | 'medium' | 'high' | 'critical';
  source: string;
  timestamp: Date;
  status: 'active' | 'resolved' | 'investigating';
  description: string;
}

// Mock data
const mockRules: SecurityRule[] = [
  {
    id: 'rule-1',
    name: 'Block Overseas IPs',
    type: 'firewall',
    status: 'active',
    action: 'deny',
    hits: 1523,
    lastTriggered: new Date(Date.now() - 300000),
    severity: 'high',
  },
  {
    id: 'rule-2',
    name: 'Rate Limit API',
    type: 'rate-limit',
    status: 'active',
    action: 'monitor',
    hits: 342,
    lastTriggered: new Date(Date.now() - 900000),
    severity: 'medium',
  },
  {
    id: 'rule-3',
    name: 'Enforce TLS 1.3',
    type: 'encryption',
    status: 'active',
    action: 'deny',
    hits: 89,
    lastTriggered: new Date(Date.now() - 1800000),
    severity: 'high',
  },
  {
    id: 'rule-4',
    name: 'Admin Access Control',
    type: 'access-control',
    status: 'active',
    action: 'allow',
    hits: 12,
    lastTriggered: new Date(Date.now() - 3600000),
    severity: 'critical',
  },
];

const mockIncidents: SecurityIncident[] = [
  {
    id: 'inc-1',
    type: 'anomaly',
    severity: 'medium',
    source: '192.168.1.45',
    timestamp: new Date(Date.now() - 600000),
    status: 'investigating',
    description: 'Unusual API access pattern detected',
  },
  {
    id: 'inc-2',
    type: 'policy-violation',
    severity: 'low',
    source: 'user-123',
    timestamp: new Date(Date.now() - 1200000),
    status: 'resolved',
    description: 'Attempted access to restricted resource',
  },
];

const getSeverityBadge = (severity: string) => {
  switch (severity) {
    case 'critical': return 'danger';
    case 'high': return 'danger';
    case 'medium': return 'warning';
    case 'low': return 'secondary';
    default: return 'secondary';
  }
};

const getTypeIcon = (type: string) => {
  switch (type) {
    case 'firewall': return '🔥';
    case 'access-control': return '🔐';
    case 'rate-limit': return '⏱️';
    case 'encryption': return '🔒';
    default: return '🛡️';
  }
};

export const MobileMCPSecurityGateway: React.FC = () => {
  const [selectedRule, setSelectedRule] = useState<string | null>(null);
  const [showIncidents, setShowIncidents] = useState(false);

  const securityStats = {
    totalRules: mockRules.length,
    activeRules: mockRules.filter(r => r.status === 'active').length,
    totalHits: mockRules.reduce((sum, rule) => sum + rule.hits, 0),
    activeIncidents: mockIncidents.filter(i => i.status !== 'resolved').length,
  };

  return (
    <div className="space-y-4">
      {/* Security Status */}
      <Card className="bg-gradient-to-r from-green-600/10 to-blue-600/10 border-green-600/30">
        <div className="p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-status-online rounded-full animate-pulse" />
              <span className="font-semibold text-console-text">Security Status: SECURE</span>
            </div>
            <Shield className="w-5 h-5 text-status-online" />
          </div>
          <p className="text-xs text-console-muted">All security protocols active</p>
        </div>
      </Card>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-3">
        <Card className="bg-gray-900">
          <div className="p-4">
            <div className="flex items-center justify-between mb-2">
              <Lock className="w-5 h-5 text-primary-500" />
            </div>
            <div className="text-xl font-semibold text-console-text">
              {securityStats.activeRules}/{securityStats.totalRules}
            </div>
            <div className="text-xs text-console-muted">Active Rules</div>
          </div>
        </Card>

        <Card className="bg-gray-900">
          <div className="p-4">
            <div className="flex items-center justify-between mb-2">
              <Activity className="w-5 h-5 text-warning-500" />
            </div>
            <div className="text-xl font-semibold text-console-text">
              {securityStats.totalHits.toLocaleString()}
            </div>
            <div className="text-xs text-console-muted">Rule Hits</div>
          </div>
        </Card>

        <Card className="bg-gray-900">
          <div className="p-4">
            <div className="flex items-center justify-between mb-2">
              <AlertTriangle className="w-5 h-5 text-danger-500" />
            </div>
            <div className="text-xl font-semibold text-console-text">
              {securityStats.activeIncidents}
            </div>
            <div className="text-xs text-console-muted">Active Incidents</div>
          </div>
        </Card>

        <Card className="bg-gray-900">
          <div className="p-4">
            <div className="flex items-center justify-between mb-2">
              <CheckCircle2 className="w-5 h-5 text-success-500" />
            </div>
            <div className="text-xl font-semibold text-console-text">
              99.9%
            </div>
            <div className="text-xs text-console-muted">Uptime</div>
          </div>
        </Card>
      </div>

      {/* Toggle View */}
      <div className="flex gap-2">
        <Button 
          variant={!showIncidents ? "primary" : "secondary"} 
          size="sm" 
          className="flex-1"
          onClick={() => setShowIncidents(false)}
        >
          Security Rules
        </Button>
        <Button 
          variant={showIncidents ? "primary" : "secondary"} 
          size="sm" 
          className="flex-1"
          onClick={() => setShowIncidents(true)}
        >
          Incidents ({securityStats.activeIncidents})
        </Button>
      </div>

      {/* Security Rules View */}
      {!showIncidents && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-console-text">Active Rules</h3>
            <Button variant="ghost" size="sm">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
          
          {mockRules.map((rule) => (
            <Card 
              key={rule.id} 
              className={cn(
                "bg-gray-900 cursor-pointer transition-all",
                selectedRule === rule.id && "ring-2 ring-primary-600"
              )}
              onClick={() => setSelectedRule(rule.id === selectedRule ? null : rule.id)}
            >
              <div className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{getTypeIcon(rule.type)}</span>
                    <div>
                      <h4 className="font-medium text-console-text text-sm">{rule.name}</h4>
                      <p className="text-xs text-console-muted capitalize">{rule.type}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={getSeverityBadge(rule.severity)} className="text-xs">
                      {rule.severity}
                    </Badge>
                    {rule.status === 'active' ? (
                      <Eye className="w-4 h-4 text-success-500" />
                    ) : (
                      <EyeOff className="w-4 h-4 text-gray-500" />
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <div className="text-xs text-console-muted">Action</div>
                    <div className={cn(
                      "text-sm font-medium capitalize",
                      rule.action === 'deny' ? "text-danger-500" :
                      rule.action === 'allow' ? "text-success-500" :
                      "text-warning-500"
                    )}>
                      {rule.action}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-console-muted">Hits</div>
                    <div className="text-sm font-medium text-console-text">
                      {rule.hits.toLocaleString()}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-console-muted">Last Hit</div>
                    <div className="text-sm font-medium text-console-text">
                      {rule.lastTriggered ? formatRelativeTime(rule.lastTriggered) : 'Never'}
                    </div>
                  </div>
                </div>

                {selectedRule === rule.id && (
                  <div className="border-t border-gray-800 pt-3 mt-3">
                    <div className="flex gap-2">
                      <Button variant="secondary" size="sm" className="flex-1">
                        <Settings className="w-3 h-3 mr-1" />
                        Configure
                      </Button>
                      <Button 
                        variant={rule.status === 'active' ? "danger" : "success"} 
                        size="sm" 
                        className="flex-1"
                      >
                        {rule.status === 'active' ? (
                          <>
                            <Ban className="w-3 h-3 mr-1" />
                            Disable
                          </>
                        ) : (
                          <>
                            <CheckCircle2 className="w-3 h-3 mr-1" />
                            Enable
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Incidents View */}
      {showIncidents && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-console-text">Security Incidents</h3>
            <Button variant="ghost" size="sm">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
          
          {mockIncidents.map((incident) => (
            <Card key={incident.id} className="bg-gray-900">
              <div className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className={cn(
                      "w-4 h-4",
                      incident.severity === 'critical' || incident.severity === 'high' ? "text-danger-500" :
                      incident.severity === 'medium' ? "text-warning-500" :
                      "text-gray-500"
                    )} />
                    <span className="font-medium text-console-text text-sm capitalize">
                      {incident.type.replace('-', ' ')}
                    </span>
                  </div>
                  <Badge 
                    variant={incident.status === 'resolved' ? 'success' : 'warning'} 
                    className="text-xs"
                  >
                    {incident.status}
                  </Badge>
                </div>
                
                <p className="text-xs text-console-muted mb-2">{incident.description}</p>
                
                <div className="flex items-center justify-between text-xs">
                  <span className="text-console-muted">Source: {incident.source}</span>
                  <span className="text-console-muted">{formatRelativeTime(incident.timestamp)}</span>
                </div>
              </div>
            </Card>
          ))}
          
          {mockIncidents.length === 0 && (
            <Card className="bg-gray-900">
              <div className="p-8 text-center">
                <CheckCircle2 className="w-8 h-8 text-success-500 mx-auto mb-2" />
                <p className="text-sm text-console-text">No active incidents</p>
                <p className="text-xs text-console-muted">All systems secure</p>
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Quick Actions */}
      <Card className="bg-gray-900">
        <div className="p-4">
          <h3 className="text-sm font-medium text-console-text mb-3">Security Actions</h3>
          <div className="grid grid-cols-2 gap-2">
            <Button variant="danger" size="sm" className="flex items-center justify-center gap-2">
              <Ban className="w-4 h-4" />
              Block All
            </Button>
            <Button variant="secondary" size="sm" className="flex items-center justify-center gap-2">
              <Key className="w-4 h-4" />
              Rotate Keys
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};